import React from 'react';
import { Box, ButtonBase, Chip, IconButton, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import type { SvgIconComponent } from '@mui/icons-material';

interface GroupHeaderProps {
  icon: SvgIconComponent;
  iconColor?: string;
  label: string;
  count?: number;
  expanded: boolean;
  onToggle: () => void;
  onTextClick?: () => void;
  secondaryText?: React.ReactNode;
}

export const GroupHeader = React.memo(function GroupHeader({
  icon: Icon,
  iconColor,
  label,
  count,
  expanded,
  onToggle,
  onTextClick,
  secondaryText,
}: GroupHeaderProps) {
  const labelContent = (
    <>
      <Icon
        sx={{
          fontSize: 16,
          color: iconColor ?? 'text.secondary',
          flexShrink: 0,
        }}
      />
      <Typography
        className="group-label"
        variant="body2"
        sx={{
          fontWeight: 600,
          transition: 'color 150ms ease',
        }}
      >
        {label}
      </Typography>
    </>
  );

  return (
    <Box
      data-testid="group-header"
      aria-expanded={expanded}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        pl: { xs: 0.5, sm: 0.75 },
        pr: { xs: 1.5, sm: 2 },
        py: 1,
        minHeight: 40,
        bgcolor: 'action.hover',
      }}
    >
      <IconButton
        size="small"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label="Toggle group"
        sx={{ p: 0, flexShrink: 0 }}
      >
        <ChevronRightIcon
          fontSize="small"
          sx={{
            color: 'text.secondary',
            transition: 'transform 150ms ease',
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        />
      </IconButton>
      {onTextClick ? (
        <ButtonBase
          onClick={onTextClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            '&:hover .group-label': { color: 'primary.main' },
          }}
        >
          {labelContent}
        </ButtonBase>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {labelContent}
        </Box>
      )}
      <Box sx={{ flex: 1 }} />
      {count !== undefined && (
        <Chip
          label={count}
          size="small"
          sx={{
            display: { xs: 'none', sm: 'inline-flex' },
            typography: 'caption',
            height: 22,
            minWidth: 28,
            bgcolor: 'action.hover',
            color: 'text.secondary',
            border: 'none',
            flexShrink: 0,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      )}
      {secondaryText && (
        <Box sx={{ ml: 1, flexShrink: 0 }}>{secondaryText}</Box>
      )}
    </Box>
  );
});
